import { useEffect } from 'react';

/**
 * Tracks the pointer and writes its eased position to CSS custom properties on
 * <html>, so every consumer is pure CSS and inherits automatically:
 *
 *   --gx / --gy   viewport position in px, for the spotlight mask
 *   --px / --py   signed -0.5..0.5 offset from centre, for parallax transforms
 *
 * Ported from the landing page (`xonera-landing-page/src/hooks/usePointerParallax.ts`)
 * so the app's instrument grid behaves identically to the one it is modelled on.
 * The only change is that the landing page's separate `useReducedMotion` hook is
 * inlined — this repo has no equivalent, and one media query does not warrant a
 * new shared hook.
 *
 * Coordinates are VIEWPORT relative, not page relative, because the grid that
 * consumes them is `fixed`. Page coordinates would make the spotlight drift away
 * from the cursor as soon as you scrolled.
 *
 * The rAF loop only runs while the values are catching up, so an idle page costs
 * nothing. Skipped entirely for reduced motion and coarse pointers — on those,
 * the consuming CSS falls back to its static centred state.
 *
 * @param enabled pass false to opt out entirely (no listener, no properties).
 */
export function usePointerParallax(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const root = document.documentElement;

    // Start centred so the first move eases in rather than snapping.
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let curX = targetX;
    let curY = targetY;
    let raf = 0;

    const write = () => {
      root.style.setProperty('--gx', `${curX.toFixed(1)}px`);
      root.style.setProperty('--gy', `${curY.toFixed(1)}px`);
      root.style.setProperty(
        '--px',
        (curX / window.innerWidth - 0.5).toFixed(4)
      );
      root.style.setProperty(
        '--py',
        (curY / window.innerHeight - 0.5).toFixed(4)
      );
    };

    const tick = () => {
      const dx = targetX - curX;
      const dy = targetY - curY;

      // Converged: stop rather than spinning on sub-pixel deltas.
      if (Math.abs(dx) < 0.3 && Math.abs(dy) < 0.3) {
        curX = targetX;
        curY = targetY;
        write();
        raf = 0;
        return;
      }

      curX += dx * 0.12;
      curY += dy * 0.12;
      write();
      raf = requestAnimationFrame(tick);
    };

    const kick = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const onMove = (e: PointerEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;
      kick();
    };

    write();
    window.addEventListener('pointermove', onMove, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
      for (const v of ['--gx', '--gy', '--px', '--py']) {
        root.style.removeProperty(v);
      }
    };
  }, [enabled]);
}
